import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

interface FormData {
  name: string;
  practiceName: string;
  email: string;
  phone: string;
  websiteUrl: string;
  monthlyCollections: string;
  currentNewPatients: string;
  targetNewPatients: string;
  biggestBottleneck: string;
  preferredTiming: string;
}

const initial: FormData = {
  name: "",
  practiceName: "",
  email: "",
  phone: "",
  websiteUrl: "",
  monthlyCollections: "",
  currentNewPatients: "",
  targetNewPatients: "",
  biggestBottleneck: "",
  preferredTiming: "",
};

const CheckupForm = () => {
  const [form, setForm] = useState<FormData>(initial);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const set = <K extends keyof FormData>(k: K, v: FormData[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  const canAdvance =
    form.name && form.practiceName && form.email && form.phone && form.websiteUrl;

  const canSubmit =
    form.monthlyCollections && form.currentNewPatients && form.targetNewPatients;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    // Fire webhook in background — don't block navigation
    supabase.functions.invoke("ghl-webhook", {
      body: {
        ...form,
        source: "growth-checkup",
      },
    }).catch((err) => console.error("Webhook error:", err));

    navigate("/GetGrowthScore/thank-you", {
      state: { name: form.name, email: form.email },
    });
  };

  return (
    <section id="checkup-form" className="py-14 md:py-20 bg-background">
      <div className="max-w-xl mx-auto section-padding">
        {/* Trust strip near form */}
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mb-6 text-xs text-muted-foreground">
          <span>✓ No obligation</span>
          <span>✓ Delivered in ~3 business days</span>
          <span>✓ Built by a dental-focused team</span>
        </div>

        <div className="text-center mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground tracking-tight mb-2">
            Request Your New Patient Growth Checkup
          </h2>
          <p className="text-muted-foreground text-sm">
            Tell us a little about your practice so we can review the right things.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-card rounded-2xl p-6 md:p-8 border border-border shadow-lg">
          {/* Step indicator */}
          <div className="flex items-center justify-center gap-2 mb-8">
            {[1, 2].map((s) => (
              <div key={s} className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                  step >= s ? "bg-accent text-accent-foreground" : "bg-muted text-muted-foreground"
                }`}>
                  {s}
                </div>
                {s < 2 && <div className="w-12 h-0.5 bg-border" />}
              </div>
            ))}
          </div>

          {step === 1 && (
            <div className="space-y-5">
              <p className="font-semibold text-foreground text-lg">Your Details</p>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="ck-name">Full Name *</Label>
                  <Input id="ck-name" value={form.name} onChange={(e) => set("name", e.target.value)} required />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ck-practice">Practice Name *</Label>
                  <Input id="ck-practice" value={form.practiceName} onChange={(e) => set("practiceName", e.target.value)} required />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="ck-email">Email Address *</Label>
                  <Input id="ck-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ck-phone">Mobile Number *</Label>
                  <Input id="ck-phone" type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} required />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="ck-website">Website URL *</Label>
                <Input id="ck-website" value={form.websiteUrl} onChange={(e) => set("websiteUrl", e.target.value)} placeholder="yourpractice.com" required />
              </div>

              <button
                type="button"
                onClick={() => canAdvance && setStep(2)}
                className="w-full bg-accent text-accent-foreground py-3 rounded-lg font-semibold hover:bg-accent/90 transition-all"
              >
                Continue →
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <p className="font-semibold text-foreground text-lg">Practice Context</p>

              <div className="space-y-1.5">
                <Label>Approximate Monthly Collections *</Label>
                <Select value={form.monthlyCollections} onValueChange={(v) => set("monthlyCollections", v)}>
                  <SelectTrigger><SelectValue placeholder="Select range" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="under-80k">Under $80K</SelectItem>
                    <SelectItem value="80k-100k">$80K – $100K</SelectItem>
                    <SelectItem value="100k-150k">$100K – $150K</SelectItem>
                    <SelectItem value="150k-250k">$150K – $250K</SelectItem>
                    <SelectItem value="250k-plus">$250K+</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="ck-current">Current New Patients / Month *</Label>
                  <Input id="ck-current" value={form.currentNewPatients} onChange={(e) => set("currentNewPatients", e.target.value)} placeholder="e.g. 25" required />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ck-target">Target New Patients / Month *</Label>
                  <Input id="ck-target" value={form.targetNewPatients} onChange={(e) => set("targetNewPatients", e.target.value)} placeholder="e.g. 50" required />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="ck-bottleneck">Biggest Growth Bottleneck *</Label>
                <Textarea id="ck-bottleneck" value={form.biggestBottleneck} onChange={(e) => set("biggestBottleneck", e.target.value)} placeholder="What do you think is holding growth back?" rows={2} required />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="ck-timing">Preferred Timing for Review Call</Label>
                <Input id="ck-timing" value={form.preferredTiming} onChange={(e) => set("preferredTiming", e.target.value)} placeholder="Optional — e.g. mornings, after 2pm" />
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="px-6 py-3 rounded-lg font-medium border border-border text-foreground hover:bg-muted transition-all"
                >
                  <ArrowLeft className="w-4 h-4 inline mr-1" />Back
                </button>
                <button
                  type="submit"
                  disabled={submitting || !canSubmit}
                  className="flex-1 bg-accent text-accent-foreground py-3 rounded-lg font-semibold hover:bg-accent/90 transition-all inline-flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</>
                  ) : (
                    <>Request My Growth Checkup <ArrowRight className="w-4 h-4" /></>
                  )}
                </button>
              </div>
            </div>
          )}
        </form>

        <p className="text-muted-foreground/50 text-xs text-center mt-4">
          We use this information to prepare a more useful review of your practice. We do not ask for anything we do not need.
        </p>
      </div>
    </section>
  );
};

export default CheckupForm;
